import type { ReactElement } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { SimpleChipItem } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { InputSettingSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/InputSettingSection";
import type { ICampaignSettingField } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/types";

interface IBrandSettingProps extends ICampaignSettingField {
    selectedBrands: string[];
    setSelectedBrands: (brands: string[]) => void;
}

export const BrandsSetting = ({ selectedBrands, setSelectedBrands, isReadOnly }: IBrandSettingProps) => {
    const selectedBrandsRef = useRef<string[]>(selectedBrands);
    const [brandToAdd, setBrandToAdd] = useState<string>("");
    const [error, setError] = useState<string>("");

    useEffect(() => {
        selectedBrandsRef.current = selectedBrands;
    }, [selectedBrands]);

    const onApplyClick = useCallback((): void => {
        setSelectedBrands([...selectedBrands, brandToAdd]);
        setBrandToAdd("");
    }, [selectedBrands, setSelectedBrands, brandToAdd, setBrandToAdd]);

    const isBrandValid = useCallback((brand: string): boolean => {
        return !selectedBrandsRef.current.some((b) => b.toLowerCase() === brand.toLowerCase());
    }, []);

    const onBrandToAddChange = useCallback(
        (value: string): void => {
            const trimmedValue = value.trim();
            if (!isBrandValid(trimmedValue)) {
                setError(translate("llmTracker.campaignQuery.edit.brands.error.duplicate"));
                return;
            }

            setError("");
            setBrandToAdd(trimmedValue);
        },
        [isBrandValid, setError, setBrandToAdd],
    );

    const deleteBrand = useCallback(
        (brand: string): void => {
            const newBrandsList = selectedBrands.filter((b) => b !== brand);
            setSelectedBrands(newBrandsList);

            if (!newBrandsList?.length) {
                setError(translate("llmTracker.campaignQuery.edit.brands.error.empty"));
            }
        },
        [selectedBrands, setSelectedBrands, setError],
    );

    const renderChips = useCallback(
        (): ReactElement => (
            <Flex flexWrap="wrap">
                {selectedBrands.map((brand) => (
                    <SimpleChipItem
                        key={brand}
                        text={brand}
                        hideActionButton={isReadOnly}
                        onCloseItem={() => deleteBrand(brand)}
                    />
                ))}
            </Flex>
        ),
        [selectedBrands, isReadOnly, deleteBrand],
    );

    return isReadOnly ? (
        <Flex
            flexDirection="column"
            gap="12px"
        >
            <Text
                fontVariant="subtitle2"
                color={colorsPalettes.carbon[500]}
            >
                {translate("llmTracker.campaignQuery.edit.brands.title.read-only")}
            </Text>
            {renderChips()}
        </Flex>
    ) : (
        <InputSettingSection
            title={translate("llmTracker.campaignQuery.edit.brands.title")}
            fieldPlaceholder={translate("llmTracker.campaignQuery.edit.brands.placeholder")}
            onValueChange={onBrandToAddChange}
            onApply={brandToAdd ? onApplyClick : undefined}
            error={error}
        >
            {renderChips()}
        </InputSettingSection>
    );
};
